import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { IVidroCor, ISelectOption, SIM_NAO_OPTIONS } from "../types/vidro.types";

// Estilos padronizados para inputs (igual à aba GERAL)
const inputSx = {
  backgroundColor: "#f0f0f0",
  borderRadius: 0,
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiInputBase-input": {
    padding: "20px 14px 8px 14px",
  },
  minHeight: 56,
};

const selectSx = {
  backgroundColor: "#f0f0f0",
  borderRadius: 0,
  minHeight: 56,
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-select": {
    padding: "20px 14px 8px 14px",
  },
};

const labelSx = {
  color: "#3C90AD",
  fontWeight: 500,
  fontSize: "0.8rem",
  "&.Mui-focused": {
    color: "#3C90AD",
  },
};

interface TabCoresProps {
  cores: IVidroCor[];
  onChange: (cores: IVidroCor[]) => void;
  coresLista: ISelectOption[];
}

// Função para validar range 0-100
const validarRange = (valor: number): number => {
  if (isNaN(valor)) return 0;
  return Math.max(0, Math.min(100, valor));
};

export const TabCores: React.FC<TabCoresProps> = ({
  cores,
  onChange,
  coresLista,
}) => {
  const [itemParaRemover, setItemParaRemover] = useState<number | null>(null);

  // Verificar duplicatas de cor
  const verificarCorDuplicada = (idCor: number, ordem?: number): boolean => {
    return cores.some(
      (c) => c.idCor === idCor && c.ordem !== ordem
    );
  };

  const handleAdicionar = () => {
    const novaOrdem = cores.length > 0
      ? Math.max(...cores.map((c) => c.ordem || 0)) + 1
      : 1;

    onChange([
      {
        ordem: novaOrdem,
        idCor: 0,
        corDescricao: "",
        fs: 0,
        ae: 0,
        tt: 0,
        tl: 0,
        bc: 0,
        refInt: 0,
        refExt: 0,
        ativo: true, // Default: SIM
      },
      ...cores, // Novo item no topo
    ]);
  };

  const handleAlterarCampo = (
    ordem: number,
    campo: keyof IVidroCor,
    valor: any
  ) => {
    onChange(
      cores.map((c) =>
        c.ordem === ordem ? { ...c, [campo]: valor } : c
      )
    );
  };

  const handleCorChange = (ordem: number, idCor: number) => {
    const corSelecionada = coresLista.find(c => c.id === idCor);
    onChange(
      cores.map((c) =>
        c.ordem === ordem
          ? { ...c, idCor: idCor, corDescricao: corSelecionada?.nome || "" }
          : c
      )
    );
  };

  const handleRemover = (ordem: number) => {
    onChange(cores.filter((c) => c.ordem !== ordem));
    setItemParaRemover(null);
  };

  // Reordenar cores após remoção
  React.useEffect(() => {
    const coresReordenadas = cores.map((c, index) => ({
      ...c,
      ordem: index + 1,
    }));
    if (coresReordenadas.some((c, i) => c.ordem !== i + 1)) {
      onChange(coresReordenadas);
    }
  }, [cores.length]);

  return (
    <Box>
      {/* Cabeçalho com botão + ADICIONAR COR */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ color: "#3C90AD", fontWeight: 600 }}>
          Cores do Vidro
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdicionar}
          sx={{
            backgroundColor: "#3C90AD",
            "&:hover": { backgroundColor: "#2d6f87" },
          }}
        >
          + ADICIONAR COR
        </Button>
      </Box>

      {/* Lista de Cores */}
      {cores.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center", backgroundColor: "#fafafa" }}>
          <Typography variant="body2" color="text.secondary">
            Nenhuma cor adicionada.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {cores.map((cor) => {
            const isCorDuplicada = cor.idCor > 0 && verificarCorDuplicada(cor.idCor, cor.ordem);

            return (
              <Paper
                key={cor.ordem}
                sx={{
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  {/* Coluna Ordem */}
                  <Grid item xs={1}>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, color: "#3C90AD", textAlign: "center" }}
                    >
                      {cor.ordem}
                    </Typography>
                  </Grid>

                  {/* Coluna Cor */}
                  <Grid item xs={12} sm={2}>
                    <FormControl fullWidth required error={isCorDuplicada}>
                      <InputLabel sx={labelSx}>Cor</InputLabel>
                      <Select
                        value={cor.idCor || ""}
                        label="Cor"
                        onChange={(e) => handleCorChange(cor.ordem!, parseInt(e.target.value as string))}
                        sx={selectSx}
                      >
                        <MenuItem value="">
                          <em>Selecione...</em>
                        </MenuItem>
                        {coresLista.map((opt) => (
                          <MenuItem key={opt.id} value={opt.id}>
                            {opt.nome}
                          </MenuItem>
                        ))}
                      </Select>
                      {isCorDuplicada && (
                        <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5 }}>
                          Esta cor já foi adicionada.
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Coluna FS */}
                  <Grid item xs={6} sm={1}>
                    <Tooltip title="Fator Solar">
                      <TextField
                        fullWidth
                        label="FS"
                        type="number"
                        value={cor.fs || ""}
                        onChange={(e) => {
                          const valor = validarRange(parseFloat(e.target.value) || 0);
                          handleAlterarCampo(cor.ordem!, "fs", valor);
                        }}
                        inputProps={{ min: 0, max: 100 }}
                        placeholder="00"
                        InputLabelProps={{ sx: labelSx }}
                        sx={inputSx}
                      />
                    </Tooltip>
                  </Grid>

                  {/* Coluna AE */}
                  <Grid item xs={6} sm={1}>
                    <Tooltip title="Absorção de Energia">
                      <TextField
                        fullWidth
                        label="AE"
                        type="number"
                        value={cor.ae || ""}
                        onChange={(e) => {
                          const valor = validarRange(parseFloat(e.target.value) || 0);
                          handleAlterarCampo(cor.ordem!, "ae", valor);
                        }}
                        inputProps={{ min: 0, max: 100 }}
                        placeholder="00"
                        InputLabelProps={{ sx: labelSx }}
                        sx={inputSx}
                      />
                    </Tooltip>
                  </Grid>

                  {/* Coluna TT */}
                  <Grid item xs={6} sm={1}>
                    <Tooltip title="Transmissão Térmica">
                      <TextField
                        fullWidth
                        label="TT"
                        type="number"
                        value={cor.tt || ""}
                        onChange={(e) => {
                          const valor = validarRange(parseFloat(e.target.value) || 0);
                          handleAlterarCampo(cor.ordem!, "tt", valor);
                        }}
                        inputProps={{ min: 0, max: 100 }}
                        placeholder="00"
                        InputLabelProps={{ sx: labelSx }}
                        sx={inputSx}
                      />
                    </Tooltip>
                  </Grid>

                  {/* Coluna TL */}
                  <Grid item xs={6} sm={1}>
                    <Tooltip title="Transmissão Luminosa">
                      <TextField
                        fullWidth
                        label="TL"
                        type="number"
                        value={cor.tl || ""}
                        onChange={(e) => {
                          const valor = validarRange(parseFloat(e.target.value) || 0);
                          handleAlterarCampo(cor.ordem!, "tl", valor);
                        }}
                        inputProps={{ min: 0, max: 100 }}
                        placeholder="00"
                        InputLabelProps={{ sx: labelSx }}
                        sx={inputSx}
                      />
                    </Tooltip>
                  </Grid>

                  {/* Coluna BC */}
                  <Grid item xs={6} sm={1}>
                    <Tooltip title="Bloqueio de Calor">
                      <TextField
                        fullWidth
                        label="BC"
                        type="number"
                        value={cor.bc || ""}
                        onChange={(e) => {
                          const valor = validarRange(parseFloat(e.target.value) || 0);
                          handleAlterarCampo(cor.ordem!, "bc", valor);
                        }}
                        inputProps={{ min: 0, max: 100 }}
                        placeholder="00"
                        InputLabelProps={{ sx: labelSx }}
                        sx={inputSx}
                      />
                    </Tooltip>
                  </Grid>

                  {/* Coluna REF.INT */}
                  <Grid item xs={6} sm={1}>
                    <Tooltip title="Reflexão Interna">
                      <TextField
                        fullWidth
                        label="REF.INT"
                        type="number"
                        value={cor.refInt || ""}
                        onChange={(e) => {
                          const valor = validarRange(parseFloat(e.target.value) || 0);
                          handleAlterarCampo(cor.ordem!, "refInt", valor);
                        }}
                        inputProps={{ min: 0, max: 100 }}
                        placeholder="00"
                        InputLabelProps={{ sx: labelSx }}
                        sx={inputSx}
                      />
                    </Tooltip>
                  </Grid>

                  {/* Coluna REF.EXT */}
                  <Grid item xs={6} sm={1}>
                    <Tooltip title="Reflexão Externa">
                      <TextField
                        fullWidth
                        label="REF.EXT"
                        type="number"
                        value={cor.refExt || ""}
                        onChange={(e) => {
                          const valor = validarRange(parseFloat(e.target.value) || 0);
                          handleAlterarCampo(cor.ordem!, "refExt", valor);
                        }}
                        inputProps={{ min: 0, max: 100 }}
                        placeholder="00"
                        InputLabelProps={{ sx: labelSx }}
                        sx={inputSx}
                      />
                    </Tooltip>
                  </Grid>

                  {/* Coluna ATIVO */}
                  <Grid item xs={6} sm={1}>
                    <FormControl fullWidth required>
                      <InputLabel sx={labelSx}>ATIVO</InputLabel>
                      <Select
                        value={cor.ativo !== undefined ? (cor.ativo ? "true" : "false") : "true"}
                        label="ATIVO"
                        onChange={(e) => handleAlterarCampo(cor.ordem!, "ativo", e.target.value === "true")}
                        sx={selectSx}
                      >
                        {SIM_NAO_OPTIONS.map((opt) => (
                          <MenuItem key={String(opt.value)} value={String(opt.value)}>
                            {opt.label.toUpperCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Coluna Ações */}
                  <Grid item xs={6} sm={1}>
                    <IconButton
                      onClick={() => setItemParaRemover(cor.ordem!)}
                      sx={{ color: "#f44336" }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={itemParaRemover !== null} onClose={() => setItemParaRemover(null)}>
        <DialogTitle>Remover cor</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja remover esta cor?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemParaRemover(null)}>Cancelar</Button>
          <Button
            onClick={() => itemParaRemover !== null && handleRemover(itemParaRemover)}
            color="error"
            variant="contained"
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
